import { AlertCircle, CheckCircle } from 'lucide-react';
/**
 * TwoFactorPage.jsx — Écran de saisie du code 2FA
 * Affiché après le login si l'utilisateur a la 2FA activée.
 * Le serveur a déjà envoyé le code par email.
 */
import React, { useState, useRef, useEffect } from 'react';
import { BRAND } from '../constants/theme';
import { semanticColors } from '../constants/semanticColors';

const TwoFactorPage = ({ userId, onSuccess, darkMode = false }) => {
  const [code, setCode]       = useState(['', '', '', '', '', '']);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [resent, setResent]   = useState(false);
  const inputs = useRef([]);

  const bg      = darkMode ? '#0F0F23' : '#F0F4FF';
  const cardBg  = darkMode ? '#1E1E32' : '#fff';
  const text     = darkMode ? '#E1E1E1' : '#1a1a2e';
  const textSec  = darkMode ? '#94A3B8' : '#6B7280';
  const border   = darkMode ? 'rgba(255,255,255,0.12)' : '#E2E8F0';

  // Focus auto sur le premier champ
  useEffect(() => { inputs.current[0]?.focus(); }, []);

  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return; // chiffres uniquement
    const next = [...code];
    next[i] = val;
    setCode(next);
    if (val && i < 5) inputs.current[i + 1]?.focus();
    if (next.every(d => d !== '')) handleSubmit(next.join(''));
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !code[i] && i > 0) {
      inputs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      handleSubmit(pasted);
    }
    e.preventDefault();
  };

  const handleSubmit = async (fullCode) => {
    const codeToVerify = fullCode || code.join('');
    if (codeToVerify.length !== 6) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/verify-2fa', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, code: codeToVerify }),
      });
      const data = await res.json();
      if (data.success) {
        onSuccess(data);
      } else {
        setError(data.message || 'Code incorrect');
        setCode(['', '', '', '', '', '']);
        inputs.current[0]?.focus();
      }
    } catch {
      setError('Erreur de connexion. Vérifiez votre réseau.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/api/auth/2fa/resend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId }),
      });
      const data = await res.json();
      if (data.success) {
        setResent(true);
        setCode(['', '', '', '', '', '']);
        inputs.current[0]?.focus();
        setTimeout(() => setResent(false), 6000);
      } else {
        setError(data.message || 'Erreur lors du renvoi');
      }
    } catch {
      setError('Erreur réseau — réessayez');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '48px', height: '56px', fontSize: '24px', fontWeight: '700',
    textAlign: 'center', borderRadius: '10px', outline: 'none',
    border: `2px solid ${border}`, background: darkMode ? '#282845' : '#F8FAFF',
    color: text, transition: 'border-color 0.15s', fontFamily: "'Poppins', sans-serif",
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bg, padding: '20px' }}>
      <div style={{ background: cardBg, borderRadius: '20px', padding: '48px 40px', maxWidth: '420px', width: '100%', textAlign: 'center', boxShadow: '0 8px 40px rgba(27,119,241,0.12)' }}>
        
        {/* Icône */}
        <div style={{ width: '64px', height: '64px', background: 'linear-gradient(135deg, #1B77F1, #00CCFF)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: '28px' }}>
          🔐
        </div>

        <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '22px', fontWeight: '700', color: text, margin: '0 0 8px' }}>
          Vérification en deux étapes
        </h2>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '14px', color: textSec, margin: '0 0 32px', lineHeight: 1.6 }}>
          Un code à 6 chiffres a été envoyé à votre adresse email.
          Saisissez-le ci-dessous.
        </p>

        {/* Champs OTP */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '24px' }}
          onPaste={handlePaste}>
          {code.map((digit, i) => (
            <input
              key={i}
              ref={el => inputs.current[i] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              style={{
                ...inputStyle,
                borderColor: digit ? '#1B77F1' : border,
              }}
            />
          ))}
        </div>

        {/* Erreur */}
        {error && (
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: semanticColors.redText, marginBottom: '16px', padding: '10px', background: 'rgba(239,68,68,0.08)', borderRadius: '8px' }}>
            <><AlertCircle size={13} style={{marginRight:'4px',verticalAlign:'middle'}} />{error}</>
          </div>
        )}
        {resent && (
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: semanticColors.greenText, marginBottom: '16px' }}>
            <><CheckCircle size={13} style={{marginRight:'4px',verticalAlign:'middle'}} />Nouveau code envoyé</>
          </div>
        )}

        {/* Bouton valider */}
        <button
          onClick={() => handleSubmit()}
          disabled={loading || code.some(d => !d)}
          style={{
            width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
            background: code.every(d => d) ? 'linear-gradient(135deg, #1B77F1, #00CCFF)' : (darkMode ? '#2D2D42' : '#E2E8F0'),
            color: code.every(d => d) ? '#fff' : textSec,
            fontFamily: "'Poppins', sans-serif", fontSize: '15px', fontWeight: '600',
            cursor: code.every(d => d) && !loading ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s', marginBottom: '16px',
          }}>
          {loading ? 'Vérification…' : 'Vérifier'}
        </button>

        <button onClick={handleResend} style={{ background: 'none', border: 'none', color: BRAND.blueText, fontSize: '13px', cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
          Renvoyer le code
        </button>
      </div>
    </div>
  );
};

export default TwoFactorPage;
